import { supabase } from './supabase';

export type WordPracticeMode = 'meaning_match' | 'spelling' | 'mixed';
export type WordQuestionMode = Exclude<WordPracticeMode, 'mixed'>;

export interface WordWorldProgress {
  totalAttempts: number;
  correctAttempts: number;
  accuracyPercent: number;
  learningWords: number;
  masteredWords: number;
  availableWords: number;
  lastPractisedAt: string | null;
}

export interface WordMeaningOption {
  id: string;
  text: string;
}

export interface WordPracticeQuestion {
  questionNumber: number;
  practiceMode: WordQuestionMode;
  prompt: string;
  hint: string;
  example: string;
  options: WordMeaningOption[];
}

export interface WordAnswerFeedback {
  isCorrect: boolean;
  correctAnswer: string;
  feedbackMessage: string;
  currentScore: number;
}

export interface WordPracticeResult {
  sessionId: string;
  score: number;
  questionCount: number;
  percentage: number;
  durationSeconds: number;
  completedAt: string;
}

function throwIfError(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

export async function loadWordWorldProgress(childId: string): Promise<WordWorldProgress> {
  const { data, error } = await supabase.rpc('get_word_world_progress', { p_child_id: childId });
  throwIfError(error);
  const row = Array.isArray(data) ? data[0] : data;
  return {
    totalAttempts: Number(row?.total_attempts ?? 0),
    correctAttempts: Number(row?.correct_attempts ?? 0),
    accuracyPercent: Number(row?.accuracy_percent ?? 0),
    learningWords: Number(row?.learning_words ?? 0),
    masteredWords: Number(row?.mastered_words ?? 0),
    availableWords: Number(row?.available_words ?? 0),
    lastPractisedAt: (row?.last_practised_at as string | null | undefined) ?? null,
  };
}

export async function startWordPractice(
  childId: string,
  practiceMode: WordPracticeMode,
  questionCount: number,
): Promise<string> {
  const { data, error } = await supabase.rpc('start_word_practice', {
    p_child_id: childId,
    p_practice_mode: practiceMode,
    p_question_count: questionCount,
  });
  throwIfError(error);
  if (!data) throw new Error('The Word World session could not be created.');
  return data as string;
}

export async function loadWordPracticeQuestions(sessionId: string): Promise<WordPracticeQuestion[]> {
  const { data, error } = await supabase.rpc('get_word_practice_questions', { p_session_id: sessionId });
  throwIfError(error);
  return (data ?? []).map((row: Record<string, unknown>) => ({
    questionNumber: Number(row.question_number),
    practiceMode: row.practice_mode as WordQuestionMode,
    prompt: String(row.prompt ?? ''),
    hint: String(row.hint ?? ''),
    example: String(row.example ?? ''),
    options: Array.isArray(row.options)
      ? row.options.map((option) => ({
        id: String((option as Record<string, unknown>).id ?? ''),
        text: String((option as Record<string, unknown>).text ?? ''),
      }))
      : [],
  }));
}

export async function submitWordPracticeAnswer(
  sessionId: string,
  questionNumber: number,
  submittedText: string | null,
  selectedOptionId: string | null,
  responseMs: number,
): Promise<WordAnswerFeedback> {
  const { data, error } = await supabase.rpc('submit_word_practice_answer', {
    p_session_id: sessionId,
    p_question_number: questionNumber,
    p_submitted_text: submittedText,
    p_selected_option_id: selectedOptionId,
    p_response_ms: Math.min(300000, Math.max(0, Math.round(responseMs))),
  });
  throwIfError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('The word answer could not be checked.');
  return {
    isCorrect: Boolean(row.is_correct),
    correctAnswer: String(row.correct_answer ?? ''),
    feedbackMessage: String(row.feedback_message ?? ''),
    currentScore: Number(row.current_score ?? 0),
  };
}

export async function finishWordPractice(sessionId: string): Promise<WordPracticeResult> {
  const { data, error } = await supabase.rpc('finish_word_practice', { p_session_id: sessionId });
  throwIfError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('The Word World result could not be saved.');
  return {
    sessionId: row.session_id as string,
    score: Number(row.score ?? 0),
    questionCount: Number(row.question_count ?? 0),
    percentage: Number(row.percentage ?? 0),
    durationSeconds: Number(row.duration_seconds ?? 0),
    completedAt: row.completed_at as string,
  };
}
